// @owners { team: patients-team }
import {
  type FertilityCycleMedicationType,
  FertilityCycleMedicationTypeMap,
} from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle_medication_type';
import pluralize from 'pluralize';
import { createSelector } from 'reselect';
import { getProgynyWizardEligiblePrescriptions } from './getProgynyWizardEligiblePrescriptions';
import { type EligiblePrescription } from '~shared/reducers/ui/progynyWizard';
import { type ReduxStateShared } from '~shared/types';

const DOSAGE_CHECK_STRINGS = {
  CETROTIDE_DOSAGE: (dosage: number) => `Inject ${dosage} ${pluralize('kit', dosage)} as directed`,
  GONAL_OR_MENOPUR_DOSAGE: (dosage: number) => `Inject ${dosage} IU daily as directed`,
};

export type PrescriptionDosage = {
  dosage: number | null | undefined;
  instructions: string | null | undefined;
  medicationType: FertilityCycleMedicationType;
  prescriptionID: number;
};

const getProgynyWizardMedicationDosages = (state: ReduxStateShared): Record<string, any>[] | null | undefined =>
  state.ui?.progynyWizard?.fertilityCycle?.medication_dosages;

export const getProgynyWizardDosages: (state: ReduxStateShared) => PrescriptionDosage[] = createSelector(
  [getProgynyWizardEligiblePrescriptions, getProgynyWizardMedicationDosages],
  (
    eligiblePrescriptions: EligiblePrescription[] | null | undefined,
    dosages: Record<string, any>[] | null | undefined,
  ) => {
    if (!eligiblePrescriptions || !dosages) {
      return [];
    }

    return eligiblePrescriptions
      .map((prescription) => {
        const dosageObject = dosages.find(
          (dosageObject) => dosageObject.medication_type === prescription.medicationType,
        );
        const dosage = dosageObject?.dosage;
        let instructions;

        if (dosageObject && dosage) {
          instructions =
            dosageObject.medication_type === FertilityCycleMedicationTypeMap.CETROTIDE
              ? DOSAGE_CHECK_STRINGS.CETROTIDE_DOSAGE(dosage)
              : DOSAGE_CHECK_STRINGS.GONAL_OR_MENOPUR_DOSAGE(dosage);
        }

        return {
          dosage,
          instructions,
          medicationType: prescription.medicationType,
          prescriptionID: prescription.prescriptionID,
        };
      })
      .filter((prescriptionDosage) => !!prescriptionDosage.dosage);
  },
);
